/**
 * @file How much of one page of an ArTable a Form Derive projection manages: how
 * many rows it owns, and which columns therefore carry the derived mark.
 * Consumers: `ArTableBody` (the header mark) and `ArTableControls` (the managed-row
 * split beside the row count) — both ask about the same page, so both ask here.
 * Invariants: ownership comes from the `drv~` row-id namespace via
 * `deriveProjectionOfRow`, never from a row's `deriveOwner` marker; a column earns
 * the mark only while a row it owns is on THIS page; a dotted declared column marks
 * its ROOT column, because that is the column the header renders; and an unmanaged
 * page hands back one frozen empty set, so a memoized header sees a stable prop.
 */

import type { ArRowId, TypeId } from '#types';
import { deriveProjectionOfRow } from './deriveProjectionOfRow';
import type { ArTableDeriveProjection } from './resolveArTableDeriveProjections';

const NO_DERIVED_TYPE_IDS: ReadonlySet<TypeId> = new Set<TypeId>();

type ManagedRows = {
  /** Rows of this page a projection owns. Never more than `rowIds.length`. */
  managedRowCount: number;
  /** Columns of this table the projections owning those rows fill. */
  derivedTypeIds: ReadonlySet<TypeId>;
};

const NONE: ManagedRows = Object.freeze({
  managedRowCount: 0,
  derivedTypeIds: NO_DERIVED_TYPE_IDS,
});

function resolveManagedRows(
  projections: readonly ArTableDeriveProjection[],
  rowIds: readonly ArRowId[],
): ManagedRows {
  if (projections.length === 0) return NONE;

  const derivedTypeIds = new Set<TypeId>();
  const managedRowCount = rowIds.reduce((count, rowId) => {
    const projection = deriveProjectionOfRow(projections, rowId);
    if (projection === null) return count;
    for (const column of projection.columns) derivedTypeIds.add(column.typeId);
    return count + 1;
  }, 0);

  return managedRowCount === 0 ? NONE : { managedRowCount, derivedTypeIds };
}

export { resolveManagedRows };
export type { ManagedRows };
